// Rate/Pc and GST/Pc exactly as the Sales Order prints them (backend
// docs.service salesOrderXlsx / renderSalesOrder + common/services/gst.service).
// The line amount (discounted MRP) is GST-inclusive; GST is extracted from it at
// the rate the HSN GST master gives for the line's ex-GST unit price, falling
// back to the item's own GST% when the HSN has no bands. KEEP IN SYNC with those.

export type GstBand = { minPrice: number; maxPrice: number | null; gstPct: number };
export type GstBandsByHsn = Record<string, GstBand[]>;

const r2 = (n: number) => Math.round(n * 100) / 100;

/** The band whose [min, max] holds the unit price (low→high, a boundary falls into the lower band); else the fallback. */
export function pickGstPct(bands: GstBand[] | undefined, unitTaxable: number, fallbackPct: number): number {
  if (!bands || !bands.length) return fallbackPct;
  for (const b of bands) {
    if (unitTaxable >= b.minPrice && (b.maxPrice == null || unitTaxable <= b.maxPrice)) return b.gstPct;
  }
  return fallbackPct;
}

const bandsFor = (all: GstBandsByHsn | undefined, hsn: string | null | undefined) => {
  const code = (hsn ?? '').trim();
  return code ? all?.[code] : undefined;
};

/** Only Corporate & Projects orders with "GST Extra on Rate/PC" ticked get GST added on top. */
export function gstAddedOnTop(gstOnRatePc: boolean, customerType: string | null | undefined): boolean {
  return gstOnRatePc && customerType === 'Corporate & Projects';
}

/**
 * Split a line's discounted MRP the way the Sales Order does (backend common/line-gst.ts).
 * Normally MRP is GST-inclusive: GST is extracted and `amount` = the discounted MRP.
 * With `onTop` the discounted MRP is the ex-GST value and GST is added: `amount` = it + GST.
 */
export function gstSplit(lineAmount: number, qty: number, hsn: string | null | undefined, itemGstPct: number, bands: GstBandsByHsn | undefined, onTop = false) {
  let gstPct: number, gstAmt: number, exTaxable: number, amount: number;
  if (onTop) {
    gstPct = pickGstPct(bandsFor(bands, hsn), qty > 0 ? lineAmount / qty : 0, itemGstPct);
    gstAmt = gstPct > 0 ? r2((lineAmount * gstPct) / 100) : 0;
    exTaxable = r2(lineAmount);
    amount = r2(lineAmount + gstAmt);
  } else {
    const unitTaxable = qty > 0 ? r2((lineAmount * 100) / (100 + itemGstPct)) / qty : 0;
    gstPct = pickGstPct(bandsFor(bands, hsn), unitTaxable, itemGstPct);
    gstAmt = gstPct > 0 ? r2((lineAmount * gstPct) / (100 + gstPct)) : 0;
    exTaxable = r2(lineAmount - gstAmt);
    amount = lineAmount;
  }
  return {
    gstPct,
    gstAmt,
    exTaxable,
    /** What the customer pays for the line (GST included). */
    amount,
    /** Rate/Pc on the Sales Order — without GST. */
    ratePc: r2(qty > 0 ? exTaxable / qty : exTaxable),
    gstPerPc: r2(qty > 0 ? gstAmt / qty : gstAmt),
  };
}

/**
 * The GST-inclusive per-pc price that the Sales Order prints as Rate/Pc `exRate`
 * — used when someone types a Rate/Pc. Tries each band's GST% and keeps the one
 * the master would actually pick for that price.
 */
export function inclusiveFromExRate(exRate: number, hsn: string | null | undefined, itemGstPct: number, bands: GstBandsByHsn | undefined): number {
  const list = bandsFor(bands, hsn);
  for (const g of [...new Set([...(list ?? []).map((b) => b.gstPct), itemGstPct])]) {
    const incl = exRate * (1 + g / 100);
    if (pickGstPct(list, incl / (1 + itemGstPct / 100), itemGstPct) === g) return incl;
  }
  return exRate * (1 + itemGstPct / 100);
}
